import { makeStyles } from 'tss-react/mui';

import { ORDINAL_BADGE_LARGE_PX } from './suggested-edits/OrdinalBadge';

/**
 * The column left of a position card holding its ordinal, the rail and the
 * grip. Exactly as wide as the ordinal, so the gap to the card is all
 * POSITION_GUTTER_GAP_PX.
 */
export const POSITION_GUTTER_PX = ORDINAL_BADGE_LARGE_PX;
/** Space between the gutter and the card. */
export const POSITION_GUTTER_GAP_PX = 8;
/** Where a card's left edge sits, for rows that line up with the cards. */
const CARD_INDENT_PX = POSITION_GUTTER_PX + POSITION_GUTTER_GAP_PX;

export const useRuleDetailsStyles = makeStyles<void, 'cardActions' | 'dragHandleIcon'>()(
  (theme, _params, classes) => ({
    // The same blue as the "Add Position" button under the list.
    addHeaderButton: {
      color: theme.appColors.primary,
      flexShrink: 0,
    },
    // Indented past the gutter so it lines up with the cards, not the ordinals.
    addPositionRow: {
      marginTop: theme.spacing(2),
      paddingLeft: CARD_INDENT_PX,
    },
    card: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.appColors.ruleSubtle}`,
      borderRadius: 12,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      minWidth: 0,
      padding: theme.spacing(2),
      position: 'relative',
    },
    // Pinned to the card's top-right corner and revealed on card hover, like
    // the delete icons on the rule rows.
    cardActions: {
      color: theme.appColors.secondaryType,
      display: 'flex',
      gap: theme.spacing(0.25),
      opacity: 0,
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      transition: 'opacity 120ms ease',
      zIndex: 1,
    },
    /**
     * Drag handle for reordering positions. The span covers the whole gutter
     * left of the card, so moving the cursor off the card towards the grip
     * still counts as hovering the card and the grip stays visible until it is
     * reached. The glyph sits on the rail just under the ordinal; grabbing
     * anywhere in the strip starts the drag.
     */
    // The position's name pill on the left, its suggestion chip on the right.
    cardHeader: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      minWidth: 0,
    },
    /**
     * Drag handle for reordering positions. The span covers the whole gutter
     * left of the card, so moving the cursor off the card towards the grip
     * still counts as hovering the card and the grip stays visible until it
     * is reached. The glyph sits on the rail, centred on the card; grabbing
     * anywhere in the strip starts the drag.
     */
    dragHandle: {
      alignItems: 'center',
      bottom: 0,
      color: theme.appColors.secondaryType,
      cursor: 'grab',
      display: 'flex',
      justifyContent: 'center',
      left: 0,
      position: 'absolute',
      top: 0,
      width: POSITION_GUTTER_PX,
      zIndex: 1,
    },
    /**
     * Only the glyph fades, so the hit area above stays in place at rest. Its
     * background masks the rail behind it.
     */
    dragHandleIcon: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 4,
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    field: {
      fontSize: 14,
      width: '100%',
    },
    fieldGroup: {
      minWidth: 0,
    },
    // Small caps over the field, like a form's section labels.
    fieldLabel: {
      color: theme.appColors.iconsTypeMuted,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      marginBottom: theme.spacing(0.5),
      textTransform: 'uppercase',
    },
    /**
     * The column beside the card: the ordinal at the top, level with the
     * card's name pill, and a rail running down from it alongside the card so
     * the positions read as one ordered list.
     */
    guidanceField: {
      fontSize: 14,
      width: '100%',
    },
    gutter: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: theme.spacing(2),
      position: 'relative',
    },
    gutterRail: {
      backgroundColor: theme.appColors.ruleSubtle,
      // Stops where the card's corner radius begins rather than at its edge.
      bottom: 12,
      left: '50%',
      position: 'absolute',
      // Starts a little under the ordinal.
      top: theme.spacing(6.5),
      transform: 'translateX(-50%)',
      width: 1,
    },
    panel: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      minWidth: 0,
      // The move arrows are positioned against the panel.
      position: 'relative',
    },
    panelContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      padding: theme.spacing(3),
    },
    // A ghost destination in the position list, indented past the gutter.
    positionGhostRow: {
      paddingLeft: CARD_INDENT_PX,
    },
    positionList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    positionRow: {
      [`&:hover .${classes.cardActions}, &:hover .${classes.dragHandleIcon}`]: {
        opacity: 1,
      },
      columnGap: POSITION_GUTTER_GAP_PX,
      display: 'grid',
      gridTemplateColumns: `${POSITION_GUTTER_PX}px minmax(0, 1fr)`,
      position: 'relative',
    },
    positionsHeader: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
      minWidth: 0,
      paddingLeft: 14,
      width: '100%',
    },
    positionsTitle: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.75),
      minWidth: 0,
    },
    /**
     * The sparkle at the right end of the title row, in the same quiet grey
     * as the playbook header's actions. The offset centres the 26px button
     * on the title's 30px line.
     */
    titleAction: {
      color: theme.appColors.secondaryType,
      flexShrink: 0,
      marginTop: 2,
    },
    // The rule's name, styled like the title of a task in its details panel.
    titleDecision: {
      marginTop: 3,
    },
    titleDisplay: {
      color: theme.appColors.iconsType,
      fontSize: 24,
      fontWeight: 700,
      lineHeight: '30px',
    },
    /**
     * The title's pencil, pinned to the first line beside the sparkle rather
     * than centred on a wrapped title, like the playbook header. The same
     * offset as the sparkle's centres the 26px button on the 30px line.
     */
    titleEditIcon: {
      marginTop: 2,
    },
    titleField: {
      flexGrow: 1,
      minWidth: 0,
    },
    titleInput: {
      fontSize: 24,
      fontWeight: 700,
      height: 'auto',
      lineHeight: '30px',
      padding: 0,
    },
    titlePlaceholder: {
      color: theme.appColors.secondaryType,
    },
    /**
     * The title with its pending-edit decision at the right end, like the
     * playbook title. Top-aligned so a wrapped title keeps the decision on its
     * first line; the offset centres the 24px decision on that 30px line.
     */
    titleRow: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: theme.spacing(1),
      minWidth: 0,
    },
  })
);
